import { NavLink } from "react-router-dom";
import { useAuthStore } from "../../stores/auth";
import { blueprint } from "../../blueprint";
import type { NavGroup, WebModule } from "../../modules";
import { Icon } from "../ui/icon";
import { BrandMark } from "../ui/brand-mark";

const CORE_TOP: NavGroup[] = [
  { title: "Principal", order: 0, items: [{ to: "/", label: "Accueil", icon: "home", end: true }] },
];

const CORE_BOTTOM: NavGroup[] = [
  {
    title: "Système",
    adminOnly: true,
    order: 100,
    items: [
      { to: "/utilisateurs", label: "Utilisateurs", icon: "users", end: true },
      { to: "/reglages", label: "Réglages", icon: "gear", end: true },
    ],
  },
];

export function Sidebar({ modules }: { modules: WebModule[] }) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isAdmin = user?.role === "admin";

  // Tri stable par rang : à rang égal, l'ordre des modules du blueprint.
  const groups = [...CORE_TOP, ...modules.flatMap((m) => m.nav), ...CORE_BOTTOM]
    .map((g, index) => ({ g, index, order: g.order ?? 50 }))
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ g }) => g)
    .filter((g) => !g.adminOnly || isAdmin)
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.adminOnly || isAdmin) }))
    .filter((g) => g.items.length > 0);

  return (
    <nav className="sidebar">
      <div className="sidebar__brand">
        <BrandMark />
        <span className="sidebar__brand-text">
          {blueprint.name}
          {blueprint.tagline ? (
            <span className="sidebar__brand-sub">{blueprint.tagline}</span>
          ) : null}
        </span>
      </div>

      <div className="sidebar__nav">
        {groups.map((group) => (
          <div key={group.title} className="sidebar__group">
            <div className="sidebar__group-title">{group.title}</div>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `sidebar__link${isActive ? " is-active" : ""}`}
                title={item.label}
              >
                <span className="sidebar__link-icon">
                  <Icon name={item.icon} />
                </span>
                <span className="sidebar__link-label">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </div>

      <div className="sidebar__foot">
        <NavLink
          to="/profil"
          className={({ isActive }) => `sidebar__account${isActive ? " is-active" : ""}`}
        >
          <span className="sidebar__account-avatar" aria-hidden="true">
            {(user?.name ?? user?.email ?? "?").charAt(0).toUpperCase()}
          </span>
          <span className="sidebar__account-info">
            <span className="sidebar__account-name">{user?.name ?? user?.email ?? "—"}</span>
            <span className="sidebar__account-role">
              {isAdmin ? "Administrateur" : "Utilisateur"}
            </span>
          </span>
        </NavLink>
        <button
          type="button"
          className="sidebar__logout"
          onClick={() => logout()}
          aria-label="Déconnexion"
          title="Déconnexion"
        >
          <Icon name="logout" size={16} />
          <span>Déconnexion</span>
        </button>
      </div>
    </nav>
  );
}
